import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const VerifyEmailRequired = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get email and source from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get('email') || location.state?.email || '';
  const source = searchParams.get('source'); // 'register' or 'login'

  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [autoResendAttempted, setAutoResendAttempted] = useState(false);

  // Auto-resend email if coming from login (user probably lost/deleted original email)
  useEffect(() => {
    if (email && source === 'login' && !autoResendAttempted) {
      setAutoResendAttempted(true);
      handleResendEmail();
    }
  }, [email, source, autoResendAttempted]);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendEmail = async () => {
    if (!email) {
      setError('Email address is required. Please go back to login.');
      return;
    }

    setResendLoading(true);
    setError('');
    setResendSuccess(false);

    try {
      await authAPI.resendVerification(email);
      setResendSuccess(true);
      setCountdown(60); // 60 second cooldown before allowing resend again
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center px-4 pt-20">
        <div className="max-w-md w-full">
          <div className="bg-gray-900/50 border border-purple-500/20 backdrop-blur-xl rounded-2xl p-8 shadow-2xl">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Missing Email Address</h1>
              <p className="text-gray-300 mb-6">
                We couldn't find your email address. Please go back to the login page and try again.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center px-4 pt-20">
      <div className="max-w-2xl w-full">
        <div className="bg-gray-900/50 border border-purple-500/20 backdrop-blur-xl rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-500/20 rounded-full mb-4">
              <Mail className="w-10 h-10 text-yellow-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Verify Your Email Address</h1>
            <p className="text-gray-300 text-lg">
              {source === 'login'
                ? "We've sent a new verification link to"
                : "We sent a verification link to"}
            </p>
            <p className="text-purple-400 font-semibold text-lg mt-1">
              {email}
            </p>
            {source === 'login' && resendSuccess && (
              <p className="text-green-400 text-sm mt-2">
                ✓ Fresh verification email sent!
              </p>
            )}
          </div>

          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-gray-800/50 border border-purple-500/20 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-4">What to do next:</h2>
              <ol className="space-y-3 text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    1
                  </span>
                  <span>Check your email inbox for a message from Cogsmith</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    2
                  </span>
                  <span>Click the verification link in the email</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    3
                  </span>
                  <span>You'll be redirected back to login once verified</span>
                </li>
              </ol>
            </div>

            {/* Success Message */}
            {resendSuccess && (
              <div className="bg-green-500/10 border-2 border-green-500/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-green-300 mb-1">Email Sent!</p>
                    <p className="text-sm text-gray-300">
                      We've sent a new verification email to <strong className="text-green-400">{email}</strong>. 
                      Please check your inbox and spam folder.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border-2 border-red-500/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-300 mb-1">Error</p>
                    <p className="text-sm text-gray-300">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Resend Button */}
            <div className="space-y-3">
              <button
                onClick={handleResendEmail}
                disabled={resendLoading || countdown > 0}
                className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {resendLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : countdown > 0 ? (
                  `Resend Available in ${countdown}s`
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    Resend Verification Email
                  </>
                )}
              </button>

              <button
                onClick={() => navigate('/login')}
                className="w-full px-6 py-3 border border-purple-500/50 text-purple-400 rounded-lg font-semibold hover:bg-purple-500/10 transition-all"
              >
                Back to Login
              </button>
            </div>

            {/* Help Text */}
            <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-4">
              <p className="text-sm text-gray-400 text-center">
                <strong className="text-gray-300">Didn't receive the email?</strong>
                <br />
                Check your spam folder or click the resend button above.
                <br />
                If you continue to have issues, contact us at{' '}
                <a href="mailto:info@cogsmith.ai" className="text-purple-400 hover:text-purple-300">
                  info@cogsmith.ai
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailRequired;

